/**
 * Role display and access helpers.
 *
 * Roles are stored as keys (`Role.name`, e.g. `production_team`) and have no stored label, so every
 * screen that shows one derives it from the key. This used to be a `formatRoleName` copy-pasted
 * into four components and services; it lives here once so a label change lands everywhere at
 * once.
 */

import { Role } from '../_models/user.model';
import { PROJECTS_UNBILLED_HIDE_TOTALS_ROLES } from '../_constants/role-permissions';

/**
 * Labels that cannot be derived from the key by prettifying it.
 *
 * Keyed by `Role.name`. Kept deliberately small — a key that reads correctly when title-cased
 * should NOT be listed here, or the map becomes a second, silently diverging source of truth for
 * every role in the system.
 *
 * The two entries exist because Roz asked for the internal/external distinction to be explicit in
 * the UI (SPAR-743). Changing a *label* rather than a key is the point: the stored value stays
 * `production_team`, so existing user assignments and every permission mapping keep working
 * untouched.
 */
export const ROLE_LABEL_OVERRIDES: Readonly<Record<string, string>> = Object.freeze({
  production_team: 'Production (Int.)',
  production_external: 'Production (Ext.)'
});

/**
 * Human-readable label for a role key.
 *
 * Checks the override map first, then falls back to prettifying: underscores become spaces and each
 * word is title-cased, with `fp&a` special-cased so it reads FP&A rather than Fp&a.
 *
 * @param roleName The stored role key (`Role.name`). Blank/missing yields an empty string.
 */
export function formatRoleName(roleName: string): string {
  if (!roleName) return '';

  const override = ROLE_LABEL_OVERRIDES[roleName];
  if (override) return override;

  const words = roleName.replace(/_/g, ' ').split(' ');

  return words
    .map(word => {
      const upperWord = word.toUpperCase();
      if (upperWord === 'FP&A' || upperWord === 'FPNA') {
        return 'FP&A';
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Role keys held by a user.
 *
 * `CurrentUser.roles` is typed `(Role | string)[]`. In practice the stored value is a plain array
 * of keys — `AuthService.hasRole` relies on that — but the type permits objects, so normalise
 * rather than assume and have an object silently match nothing.
 */
export function toRoleKeys(roles: readonly (Role | string)[] | null | undefined): string[] {
  if (!roles) return [];

  return roles
    .map(role => (typeof role === 'string' ? role : role?.name))
    .filter((name): name is string => !!name);
}

/**
 * Whether the Unbilled page's subtotal/grand-total header should be hidden (SPAR-743).
 *
 * True only when *every* role the user holds suppresses totals. Checking "holds any of them"
 * instead would strip the totals from someone who is, say, both an admin and production — the
 * broader role should win, since hiding aggregate figures is a restriction on production users
 * rather than a property of the page.
 *
 * A user with no roles at all gets the default (totals shown); they cannot reach the page anyway.
 */
export function shouldHideUnbilledTotals(roles: readonly (Role | string)[] | null | undefined): boolean {
  const roleKeys = toRoleKeys(roles);

  return roleKeys.length > 0 &&
    roleKeys.every(roleKey => PROJECTS_UNBILLED_HIDE_TOTALS_ROLES.includes(roleKey));
}
